import { onUnmounted, ref } from "vue"

/** Top-level check usable outside of a component setup — safe to read without invoking the composable. */
export const barcodeScannerSupported = typeof window !== "undefined" && "BarcodeDetector" in window

/**
 * Wraps the BarcodeDetector API for continuous QR/barcode scanning.
 *
 * Reads frames from a live video element at ~4fps, passing each to
 * the BarcodeDetector. Emits detected codes via a callback. Falls
 * back gracefully when the API isn't supported.
 *
 * @param onDetected - Called with the raw barcode value when a code is found.
 *
 * @example
 * ```ts
 * const scanner = useBarcodeScanner((code) => lookupBarcode(code))
 * scanner.start(videoEl)
 * // ...later
 * scanner.stop()
 * ```
 */
export function useBarcodeScanner(onDetected: (code: string) => void) {
  const isSupported = ref(barcodeScannerSupported)
  const isScanning = ref(false)

  let detector: BarcodeDetector | null = null
  let animFrame: number | null = null
  let lastScanTime = 0

  /** Minimum ms between scans to avoid hammering the detector. */
  const SCAN_INTERVAL_MS = 250

  /**
   * Dual-read confirmation — a code must be seen on this many consecutive
   * frames before firing the callback. Prevents false positives from a
   * single misread.
   */
  const CONFIRM_READS = 2
  let pendingCode: string | null = null
  let pendingReads = 0

  /** Codes already confirmed in this scan session (prevent duplicate callbacks). */
  const confirmedCodes = new Set<string>()

  function resetConfirmation() {
    pendingCode = null
    pendingReads = 0
  }

  /**
   * Start scanning frames from the given video element.
   *
   * Creates a BarcodeDetector (if supported) and begins a
   * requestAnimationFrame loop that detects barcodes at ~4fps.
   * A code must be seen on 2 consecutive frames before firing.
   */
  function start(video: HTMLVideoElement) {
    if (!isSupported.value || isScanning.value) return

    detector = new BarcodeDetector({ formats: ["qr_code", "code_128", "ean_13", "ean_8"] })
    isScanning.value = true
    confirmedCodes.clear()
    resetConfirmation()

    function scanFrame() {
      if (!isScanning.value) return

      const now = Date.now()
      if (now - lastScanTime >= SCAN_INTERVAL_MS && video.readyState >= 2) {
        lastScanTime = now
        detector!
          .detect(video)
          .then((barcodes) => {
            if (barcodes.length === 0) {
              resetConfirmation()
              return
            }

            for (const barcode of barcodes) {
              if (!barcode.rawValue || confirmedCodes.has(barcode.rawValue)) continue

              if (barcode.rawValue === pendingCode) {
                pendingReads++
              } else {
                pendingCode = barcode.rawValue
                pendingReads = 1
              }

              if (pendingReads >= CONFIRM_READS) {
                confirmedCodes.add(barcode.rawValue)
                resetConfirmation()
                onDetected(barcode.rawValue)
                return
              }
            }
          })
          .catch(() => {
            // Detection can fail on frames that are too dark/blurry — ignore
          })
      }

      animFrame = requestAnimationFrame(scanFrame)
    }

    animFrame = requestAnimationFrame(scanFrame)
  }

  /** Stop the scanning loop and release the detector. */
  function stop() {
    isScanning.value = false
    if (animFrame !== null) {
      cancelAnimationFrame(animFrame)
      animFrame = null
    }
    detector = null
    confirmedCodes.clear()
    resetConfirmation()
  }

  onUnmounted(stop)

  return { start, stop, isScanning, isSupported }
}
